import { StateCreator } from 'zustand';

import type { PendingChip } from '../../hooks/chat/useAttachmentUpload';

export type ChatView = 'inbox' | 'channel' | 'new' | 'members' | 'announcers';

export interface ChatSlice {
  isChatOpen: boolean;
  chatView: ChatView;
  activeChannelId: string | null;
  /**
   * EMR-2272: the episode whose chart is on screen, published by
   * `useSetChatContextEpisode` so the inbox can pin that episode's care-team
   * thread. Session state, like the other chat fields: never persisted.
   */
  chatContextEpisodeId: string | null;
  setChatContextEpisodeId: (id: string | null) => void;
  chatDrafts: Record<string, string>;
  openChat: () => void;
  closeChat: () => void;
  setChatView: (view: ChatView) => void;
  openChatChannel: (channelId: string) => void;
  setChatDraft: (channelId: string, draft: string) => void;
  /** EMR-1878: pending attachment chips per channel, like `chatDrafts`. */
  chatChips: Record<string, PendingChip[]>;
  setChips: (
    channelId: string,
    next: (current: PendingChip[]) => PendingChip[]
  ) => void;
}

/**
 * Chat panel state. Never added to the store's `partialize`: drafts are per
 * session by ruling, and a persisted open panel would reopen on every load.
 */
export const chatSlice: StateCreator<ChatSlice> = set => ({
  isChatOpen: false,
  chatView: 'inbox',
  activeChannelId: null,
  chatContextEpisodeId: null,
  chatDrafts: {},

  openChat: () => set({ isChatOpen: true, chatView: 'inbox' }),
  closeChat: () =>
    set({
      isChatOpen: false,
      chatView: 'inbox',
      activeChannelId: null,
    }),
  setChatView: view => set({ chatView: view }),
  setChatContextEpisodeId: id => set({ chatContextEpisodeId: id }),
  openChatChannel: channelId =>
    set({ isChatOpen: true, chatView: 'channel', activeChannelId: channelId }),
  setChatDraft: (channelId, draft) =>
    set(state => ({ chatDrafts: { ...state.chatDrafts, [channelId]: draft } })),
  chatChips: {},
  setChips: (channelId, next) =>
    set(state => ({
      chatChips: {
        ...state.chatChips,
        [channelId]: next(state.chatChips[channelId] ?? []),
      },
    })),
});
